import type { CostComponentBaseType, CostServiceTrigger } from '../generated/enums.js';

/**
 * Cost recognition (Cost Services F1-S2, ALC-4475): the provision a revenue journal books for the
 * Cost Services attached to its Billing Services, in the same transaction, date, period and book
 * (ADR 0024 rules 4, 6 and 7 applied to cost).
 */

/** Posting sources of the cost journal and of its reversal mirror (registered in JournalService). */
export const COST_PROVISION_POSTING_SOURCE = 'cost_provision';
export const COST_PROVISION_REVERSAL_POSTING_SOURCE = 'cost_provision_reversal';
/** ALC-4477: the close-out of an open provision the vendor will not bill. */
export const COST_PROVISION_CLOSE_POSTING_SOURCE = 'cost_provision_close';
/** `documentType` of the cost journal; `reference` is the source revenue journal id. */
export const COST_PROVISION_DOCUMENT_TYPE = 'COST_PROVISION';
export const COST_PROVISION_REVERSAL_DOCUMENT_TYPE = 'COST_PROVISION_REVERSAL';
export const COST_PROVISION_CLOSE_DOCUMENT_TYPE = 'COST_PROVISION_CLOSE';
/** S-6: `sys_audit_log.eventType` of a provision's reversal and close-out events. */
export const COST_PROVISION_REVERSED_EVENT = 'cost_provision_reversed';
export const COST_PROVISION_CLOSED_EVENT = 'cost_provision_closed';
/** `sourceTable` of every cost journal line; `sourceId` is the entry id. */
export const COST_RECOGNITION_ENTRY_SOURCE_TABLE = 'cost_recognition_entry';
/** Cash forecast: the label of an open provision's outflow line. */
export const COST_PROVISION_FORECAST_LABEL = 'custo provisionado';
/** D3: with no vendor term, payment is expected this many days after the competency month ends. */
export const COST_PROVISION_DEFAULT_PAYMENT_TERM_DAYS = 30;

/**
 * Status of a provision. DERIVED, never stored or edited by hand (spec story 20):
 * - REVERSED: the revenue it came from was reversed (or the provision was closed out);
 * - SETTLED: nothing open;
 * - PARTIALLY_SETTLED: some of it matched to bills;
 * - ACCRUED: open = amount.
 */
export const COST_RECOGNITION_ENTRY_STATUSES = [
  'ACCRUED',
  'PARTIALLY_SETTLED',
  'SETTLED',
  'REVERSED',
] as const;
export type CostRecognitionEntryStatus = (typeof COST_RECOGNITION_ENTRY_STATUSES)[number];

/**
 * The one rule that derives the status; amounts are exact decimal strings in the vendor currency.
 * `reversedAmount` is what revenue reversals gave back (partial NFS-e cancels leave `reversed`
 * false): the status reads on the kept part, `amount − reversedAmount`. Example: R$ 1.000,00
 * provision, R$ 600,00 given back, R$ 400,00 open → ACCRUED; a R$ 100,00 bill then makes it
 * PARTIALLY_SETTLED; everything given back → REVERSED.
 */
export function deriveCostRecognitionEntryStatus(input: {
  amount: string;
  openAmount: string;
  reversedAmount?: string;
  reversed: boolean;
}): CostRecognitionEntryStatus {
  if (input.reversed) return 'REVERSED';
  const reversed = decimalUnits(input.reversedAmount ?? '0');
  const kept = subtract(decimalUnits(input.amount), reversed);
  if (reversed.units > 0n && kept.units <= 0n) return 'REVERSED';
  const open = decimalUnits(input.openAmount);
  if (open.units <= 0n) return 'SETTLED';
  // Exact comparison of scaled integers: never a rounded number.
  return compare(open, kept) === 0 ? 'ACCRUED' : 'PARTIALLY_SETTLED';
}

/** A decimal string as an integer of `scale` decimal places (exact, no float). */
interface ScaledDecimal {
  units: bigint;
  scale: number;
}

function decimalUnits(value: string): ScaledDecimal {
  const text = value.trim();
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) throw new Error(`Not a decimal: ${value}`);
  const [, sign, whole, fraction = ''] = match;
  const units = BigInt(`${whole || '0'}${fraction}`) * (sign ? -1n : 1n);
  return { units, scale: fraction.length };
}

function rescale(value: ScaledDecimal, scale: number): bigint {
  return value.units * 10n ** BigInt(scale - value.scale);
}

function subtract(a: ScaledDecimal, b: ScaledDecimal): ScaledDecimal {
  const scale = Math.max(a.scale, b.scale);
  return { units: rescale(a, scale) - rescale(b, scale), scale };
}

function compare(a: ScaledDecimal, b: ScaledDecimal): number {
  const scale = Math.max(a.scale, b.scale);
  const diff = rescale(a, scale) - rescale(b, scale);
  return diff === 0n ? 0 : diff > 0n ? 1 : -1;
}

/**
 * Why an entry is PENDING (no journal yet). The revenue commits; the heal action posts the entry
 * once the cause is fixed (ADR 0024 rule 7). Stable codes with operator copy.
 */
export const CostRecognitionPendingReason = {
  /** The revenue journal's period is not OPEN (or the date falls outside it). */
  PERIOD_NOT_OPEN: 'PERIOD_NOT_OPEN',
  /** The book's chart version in that period does not accept postings. */
  COA_VERSION_NOT_POSTABLE: 'COA_VERSION_NOT_POSTABLE',
  /** The Cost Service has no cost account. */
  COST_ACCOUNT_MISSING: 'COST_ACCOUNT_MISSING',
  /** The Cost Service has no liability (provision) account. */
  LIABILITY_ACCOUNT_MISSING: 'LIABILITY_ACCOUNT_MISSING',
  /** The book uses another chart and the account has no mapping into it. */
  ACCOUNT_NOT_MAPPED_TO_BOOK: 'ACCOUNT_NOT_MAPPED_TO_BOOK',
  /** The Cost Service has no vendor. */
  VENDOR_MISSING: 'VENDOR_MISSING',
  /** No exchange rate for the vendor currency on the recognition date. */
  FX_RATE_NOT_FOUND: 'FX_RATE_NOT_FOUND',
  /**
   * D22 schedule: a month of a "Distribuir em N meses" plan whose competency month has not come
   * yet. Not a problem to fix: the due-month poster posts it when the month arrives.
   */
  SCHEDULED_NOT_DUE: 'SCHEDULED_NOT_DUE',
} as const;
export type CostRecognitionPendingReason =
  (typeof CostRecognitionPendingReason)[keyof typeof CostRecognitionPendingReason];

export const COST_RECOGNITION_PENDING_REASON_MESSAGES: Readonly<
  Record<CostRecognitionPendingReason, { readonly ptBR: string; readonly en: string }>
> = Object.freeze({
  PERIOD_NOT_OPEN: {
    ptBR: 'O custo não foi lançado porque o período contábil da receita não está aberto. Reabra o período ou aguarde o próximo e use "Lançar pendentes".',
    en: 'The cost was not posted because the revenue period is not open. Reopen the period, or wait for the next one, and use "Post pending".',
  },
  COA_VERSION_NOT_POSTABLE: {
    ptBR: 'O custo não foi lançado porque o plano de contas deste período não aceita lançamentos. Ative a versão do plano e use "Lançar pendentes".',
    en: 'The cost was not posted because the chart of accounts of this period does not accept postings. Activate the chart version and use "Post pending".',
  },
  COST_ACCOUNT_MISSING: {
    ptBR: 'O custo não foi lançado porque o serviço de custo não tem conta de custo. Informe a conta e use "Lançar pendentes".',
    en: 'The cost was not posted because the Cost Service has no cost account. Set the account and use "Post pending".',
  },
  LIABILITY_ACCOUNT_MISSING: {
    ptBR: 'O custo não foi lançado porque o serviço de custo não tem conta de custos provisionados a pagar. Informe a conta e use "Lançar pendentes".',
    en: 'The cost was not posted because the Cost Service has no provisioned cost liability account. Set the account and use "Post pending".',
  },
  ACCOUNT_NOT_MAPPED_TO_BOOK: {
    ptBR: 'O custo não foi lançado neste livro porque a conta do serviço de custo não tem correspondente no plano de contas do livro. Mapeie a conta e use "Lançar pendentes".',
    en: 'The cost was not posted in this book because the Cost Service account has no counterpart in the book chart. Map the account and use "Post pending".',
  },
  VENDOR_MISSING: {
    ptBR: 'O custo não foi lançado porque o serviço de custo não tem fornecedor. Informe o fornecedor e use "Lançar pendentes".',
    en: 'The cost was not posted because the Cost Service has no vendor. Set the vendor and use "Post pending".',
  },
  FX_RATE_NOT_FOUND: {
    ptBR: 'O custo não foi lançado porque não há cotação da moeda do fornecedor na data da receita. Cadastre a cotação e use "Lançar pendentes".',
    en: 'The cost was not posted because there is no exchange rate for the vendor currency on the revenue date. Add the rate and use "Post pending".',
  },
  SCHEDULED_NOT_DUE: {
    ptBR: 'Parcela agendada do valor fixo: será lançada quando o mês de competência chegar.',
    en: 'Scheduled part of the fixed amount: it posts when its competency month arrives.',
  },
});

/**
 * D49 (CFO-06): the diagnosable cause of a pending reason, persisted beside it
 * (`cost_recognition_entry.pending_detail`). Keyed by `reason` (always equal to the row's
 * `pendingReason`), versioned `v: 1`. Scalars and ids only: no raw payloads, names or personal
 * data. Set with every reason and cleared with it; rows written before D49 have a NULL detail.
 * Example: `{ v: 1, reason: 'FX_RATE_NOT_FOUND', currency: 'USD', functionalCurrency: 'BRL',
 * rateDate: '2026-10-05', refusalCode: 'SOURCE_UNAVAILABLE', ... }`.
 */
export const COST_RECOGNITION_PENDING_DETAIL_VERSION = 1;

/**
 * Why no rate: the FX authority's refusal (`NOT_PUBLISHED`, `SOURCE_UNAVAILABLE`,
 * `INVALID_REQUEST`), or `ROUNDS_TO_ZERO` when a rate exists but the functional amount rounds to
 * nothing (a D22 plan month).
 */
export type CostRecognitionFxRefusalCode =
  'NOT_PUBLISHED' | 'SOURCE_UNAVAILABLE' | 'INVALID_REQUEST' | 'ROUNDS_TO_ZERO';

/** Fields every detail carries. `competencyMonth` is `YYYY-MM`; `detectedAt` ISO 8601. */
export interface CostRecognitionPendingDetailBase {
  v: typeof COST_RECOGNITION_PENDING_DETAIL_VERSION;
  costServiceId: string;
  contractId: string | null;
  sourceJournalId: string;
  competencyMonth: string;
  detectedAt: string;
}

export type CostRecognitionPendingDetail =
  | (CostRecognitionPendingDetailBase & {
      reason: 'PERIOD_NOT_OPEN' | 'COA_VERSION_NOT_POSTABLE';
      /** The period the cost tried to post in; null when no open period exists (D22 month). */
      periodId: string | null;
      /** Its status (OPEN, CLOSED, LOCKED …); null when the period row is missing. */
      periodStatus: string | null;
      /** `YYYY-MM-DD` the journal would have carried. */
      journalDate: string;
      /** The period's chart version; null when the period row is missing. */
      versionId: string | null;
    })
  | (CostRecognitionPendingDetailBase & {
      reason: 'VENDOR_MISSING' | 'COST_ACCOUNT_MISSING' | 'LIABILITY_ACCOUNT_MISSING';
      /** The Cost Service field to fill (`costServiceId` is the config to open). */
      missing: 'vendorId' | 'costAccountId' | 'liabilityAccountId';
    })
  | (CostRecognitionPendingDetailBase & {
      reason: 'ACCOUNT_NOT_MAPPED_TO_BOOK';
      costAccountId: string;
      liabilityAccountId: string;
      /** Which of the two has no counterpart in `versionId`. */
      unmapped: ('COST' | 'LIABILITY')[];
      versionId: string;
      bookId: string;
    })
  | (CostRecognitionPendingDetailBase & {
      reason: 'FX_RATE_NOT_FOUND';
      currency: string;
      functionalCurrency: string;
      /** `YYYY-MM-DD` the rate was asked for. */
      rateDate: string;
      refusalCode: CostRecognitionFxRefusalCode;
    })
  | (CostRecognitionPendingDetailBase & {
      reason: 'SCHEDULED_NOT_DUE';
      scheduleIndex: number | null;
      scheduleCount: number | null;
      /** `YYYY-MM` of today when the month was found not due. */
      todayMonth: string;
    });

/** One provision as the API, the MCP tool and (later) the screens read it. Dates `YYYY-MM-DD`. */
export interface CostRecognitionEntryView {
  entryId: string;
  entityId: string;
  bookId: string;
  costServiceId: string;
  costServiceName: string;
  billingServiceId: string;
  contractId: string | null;
  invoiceId: string | null;
  trigger: CostServiceTrigger;
  /** The revenue journal the cost came from. */
  revenueJournalId: string;
  /** The cost journal; null while PENDING. */
  journalId: string | null;
  competencyDate: string;
  /** `YYYY-MM` of the competency date. */
  period: string;
  periodId: string;
  vendorId: string | null;
  currency: string;
  functionalCurrency: string;
  baseType: CostComponentBaseType;
  /** Snapshot of the resolved rate (fraction or amount) and its source. */
  rate: string;
  rateSource: string;
  revenueBaseAmount: string | null;
  amount: string;
  fxRate: string;
  functionalAmount: string;
  openAmount: string;
  /** Vendor units given back by revenue reversals (a partial NFS-e cancel gives back a share). */
  reversedAmount: string;
  expectedPaymentDate: string;
  costAccountId: string | null;
  liabilityAccountId: string | null;
  postingState: 'PENDING' | 'POSTED';
  pendingReason: CostRecognitionPendingReason | null;
  /**
   * D49: the diagnosable cause of `pendingReason` (ids and scalars). Null when not pending, and
   * on PENDING rows written before D49.
   */
  pendingDetail: CostRecognitionPendingDetail | null;
  /** pt-BR copy of the pending reason, for the operator and the agent. */
  pendingMessage: string | null;
  status: CostRecognitionEntryStatus;
  reversedAt: string | null;
  reversalJournalId: string | null;
  /** ALC-4477 close-out: when, why and the closing journal (null when it never posted). */
  closedAt: string | null;
  closeReason: string | null;
  closeJournalId: string | null;
  /**
   * D22 schedule: month k of N of a "Distribuir em N meses" plan (both null on every other
   * entry). A month not yet posted is PENDING with `SCHEDULED_NOT_DUE` ("Agendada"); a month the
   * full revenue reversal cancelled before it posted is reversed with no journal ("Cancelada").
   */
  scheduleIndex: number | null;
  scheduleCount: number | null;
}

/** S-6: one bill match of a provision, live or undone (with the undo reason). */
export interface CostRecognitionEntryMatchView {
  matchId: string;
  billId: string;
  lineItemId: string;
  bookId: string;
  kind: string;
  source: string;
  currency: string;
  amountApplied: string;
  openReduction: string;
  varianceAmount: string;
  /** The bill journal (or reclass journal) that relieved the provision. */
  journalId: string | null;
  matchedAt: string;
  matchedBy: string | null;
  status: 'LIVE' | 'UNDONE';
  undoneAt: string | null;
  undoneBy: string | null;
  undoReason: string | null;
  undoJournalId: string | null;
}

/**
 * S-6: one reversal or close-out the provision took, oldest first. REVERSAL: a revenue reversal
 * gave back `units` (scope FULL or PARTIAL) in `journalId` (the mirror journal; null when the entry
 * was still PENDING). CLOSE: an operator closed `units` with `reason` in `journalId`.
 */
export interface CostRecognitionEntryEventView {
  type: 'REVERSAL' | 'CLOSE';
  at: string;
  userId: string | null;
  journalId: string | null;
  journalDate: string | null;
  units: string | null;
  currency: string | null;
  reason: string | null;
  scope: 'FULL' | 'PARTIAL' | null;
  /** REVERSAL: the reversal journal of the revenue that triggered it. */
  reversalSourceJournalId: string | null;
}

/** S-6: GET /cost-recognition-entries/:id — the provision with its matches and events. */
export interface CostRecognitionEntryDetailView extends CostRecognitionEntryView {
  matches: CostRecognitionEntryMatchView[];
  events: CostRecognitionEntryEventView[];
}

/**
 * ALC-4477 aging: an open provision past its Cost Service's `staleAfterDays`, counted in calendar
 * days from the expected payment date to `asOf` (São Paulo). Stale when daysOverdue > staleAfterDays
 * (exactly N days is not yet stale). A Cost Service with no `staleAfterDays` never ages.
 */
export interface CostProvisionAgingEntry {
  entry: CostRecognitionEntryView;
  staleAfterDays: number;
  daysOverdue: number;
  /**
   * What the vendor is still owed in vendor units: open (POSTED) or amount − reversed (PENDING; 0
   * for an FX_RATE_NOT_FOUND row, whose known cost is `entry.functionalAmount`).
   */
  outstandingAmount: string;
}

/**
 * Bill-side warning of the vendor (S3, `COST_VENDOR_OPEN_PROVISIONS` in the bill history): a bill
 * of this vendor was approved with EXPENSE lines while provisions were open — maybe the invoice of
 * an aged provision booked as plain expense. Same entity, recorded on or after the competency date
 * of the vendor's oldest listed provision, bill not voided or deleted; newest first, at most 20 per
 * vendor.
 */
export interface CostProvisionAgingExpenseWarning {
  billId: string;
  vendorBillNumber: string | null;
  /** The bill's EXPENSE lines at approval. */
  lineItemIds: string[];
  recordedAt: string;
  /** e.g. "2 provisões em aberto deste fornecedor, R$ 1.400,00" (as written at approval). */
  message: string | null;
}

/**
 * D26 aging: a bill amount booked as prepaid for an expected provision that has not consumed it
 * (all of it, or the leftover when the provision came out smaller). Calendar days from the last day
 * of its competency month to `asOf`; listed when daysOverdue > staleAfterDays of its Cost Service.
 * Close it with a reason (Dr expense / Cr prepaid today) when the provision will never post.
 */
export interface CostProvisionAgingAwaitingRow {
  reservationId: string;
  billId: string;
  vendorBillNumber: string | null;
  lineItemId: string;
  entityId: string;
  costServiceId: string;
  costServiceName: string;
  /** `YYYY-MM`. */
  competencyMonth: string;
  currency: string;
  /** Reserved at approval, vendor units. */
  amount: string;
  consumedAmount: string;
  /** Still prepaid, vendor units (amount − consumed − released). */
  remainingAmount: string;
  /** Still prepaid, functional. */
  remainingFunctional: string;
  functionalCurrency: string;
  staleAfterDays: number;
  daysOverdue: number;
  createdAt: string;
}

/**
 * W3 "Vencido, não reconhecido": a D22 plan month ("Distribuir em N meses") whose competency month
 * has come (on or before the as-of month) and that nobody recognised yet: it is still
 * SCHEDULED_NOT_DUE with open 0, so no other bucket, the cash forecast or the heal sees it. The
 * monthly recognition batch (or a later revenue event of the contract) posts it. Listed whatever
 * the Cost Service's `staleAfterDays`; the amount is the plan's, not the open balance.
 */
export interface CostProvisionAgingDueRow {
  entryId: string;
  costServiceId: string;
  costServiceName: string;
  entityId: string;
  contractId: string | null;
  /** `YYYY-MM-DD`: the plan month's competency date. */
  competencyDate: string;
  /** `YYYY-MM`. */
  competencyMonth: string;
  /** This month's place in the plan (1-based) and the plan's length. */
  scheduleIndex: number;
  scheduleCount: number;
  currency: string;
  /** The plan's amount for the month, vendor units (the open balance is 0 until it posts). */
  amount: string;
}

export interface CostProvisionAgingVendorGroup {
  vendorId: string | null;
  /** POSTED provisions past their threshold, oldest first. */
  entries: CostProvisionAgingEntry[];
  /** PENDING provisions (never posted, e.g. no FX rate) past their threshold: heal or close them. */
  pending: CostProvisionAgingEntry[];
  /** Open POSTED provision per vendor currency (PENDING rows were never posted: not summed). */
  outstandingByCurrency: Record<string, string>;
  expenseWarnings: CostProvisionAgingExpenseWarning[];
  /** D26: bill amounts still prepaid for expected provisions past their threshold, oldest first. */
  awaiting: CostProvisionAgingAwaitingRow[];
  /** W3: plan months that came due and were not recognised, oldest first (never summed above). */
  dueNotRecognized: CostProvisionAgingDueRow[];
}

export interface CostProvisionAgingReport {
  asOf: string;
  vendors: CostProvisionAgingVendorGroup[];
}
